import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useScrollLock } from "../../hooks/useScrollLock";

import {
  FiX,
  FiPlus,
  FiTrash2,
  FiMusic,
  FiUsers,
  FiCalendar,
  FiMapPin,
  FiAward,
  FiChevronDown,
} from "react-icons/fi";
import { toast } from "../modern-ui/sonner";

const BandModal = ({
  activeModal,
  closeModal,
  selectedBand,
  handleBandSubmit,
}) => {
  const [bandMembers, setBandMembers] = useState([
    { id: 1, name: "", joined: "", role: "", bio: "" },
  ]);

  const [discography, setDiscography] = useState([
    { id: 1, title: "", year: "", type: "Studio album" },
  ]);

  const [achievements, setAchievements] = useState([
    {
      id: 1,
      title: "",
      year: new Date().getFullYear(),
    },
  ]);

  // State za slike benda
  const [bandImages, setBandImages] = useState([]);

  // State za form polja
  const [selectedGenre, setSelectedGenre] = useState("");
  const [isFeatured, setIsFeatured] = useState(false);

  // State za package selection
  const [selectedPackage, setSelectedPackage] = useState("Basic 1");
  const availablePackages = [
    {
      name: "Basic 1",
      percentage: 90.0,
      description: "Basic plan - bend dobija 90% prihoda",
    },
    {
      name: "Basic 2",
      percentage: 87.5,
      description: "Enhanced basic plan - bend dobija 87.5% prihoda",
    },
    {
      name: "Standard",
      percentage: 70.0,
      description: "Standard plan - bend dobija 70% prihoda",
    },
    {
      name: "Premium 1",
      percentage: 60.0,
      description: "Premium plan - bend dobija 60% prihoda",
    },
    {
      name: "Premium 2",
      percentage: 65.0,
      description: "Premium plus plan - bend dobija 65% prihoda",
    },
  ];

  // State za loading i error
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const [originalData, setOriginalData] = useState(null);

  useScrollLock(activeModal === "addBand" || activeModal === "editBand");

  // useEffect za ažuriranje state-a kada se selectedBand promeni
  useEffect(() => {
    if (selectedBand && activeModal === "editBand") {
      // Helper funkcija za parsing JSON stringova
      const parseJsonField = (field) => {
        if (typeof field === "string") {
          try {
            return JSON.parse(field);
          } catch (error) {
            console.error("Failed to parse JSON field:", field, error);
            return [];
          }
        }
        return field || [];
      };

      // Parse members JSON string
      const membersData = parseJsonField(selectedBand.members) || [];

      setBandMembers(
        Array.isArray(membersData) && membersData.length > 0
          ? membersData.map((member, index) => {
              if (typeof member === "string") {
                return {
                  id: index + 1,
                  name: member,
                  joined: "",
                  role: "",
                  bio: "",
                };
              } else if (typeof member === "object" && member !== null) {
                return {
                  id: member.id || index + 1,
                  name: member.name || "",
                  joined: member.joined || "",
                  role: member.role || "",
                  bio: member.bio || "",
                };
              }
              return {
                id: index + 1,
                name: "",
                joined: "",
                role: "",
                bio: "",
              };
            })
          : [{ id: 1, name: "", joined: "", role: "", bio: "" }]
      );

      // Pokušaj sa discography ili albums kao fallback - parsiraj JSON string
      const albumsData =
        parseJsonField(selectedBand.discography) ||
        parseJsonField(selectedBand.albums) ||
        [];

      setDiscography(
        Array.isArray(albumsData) && albumsData.length > 0
          ? albumsData.map((album, index) => ({
              id: album.id || index + 1, // Koristi postojeći id ili index + 1
              title: album.title || album.title || "",
              year: album.year || "",
              type: album.type || "Studio album",
            }))
          : [{ id: 1, title: "", year: "", type: "Studio album" }]
      );

      setAchievements(
        Array.isArray(selectedBand.achievements) &&
          selectedBand.achievements.length > 0
          ? selectedBand.achievements.map((achievement, index) => {
              if (typeof achievement === "string") {
                return {
                  id: index + 1, // Koristi index + 1 kao id
                  title: achievement,
                  year: new Date().getFullYear(),
                };
              }
              return {
                id: achievement.id || index + 1, // Koristi postojeći id ili index + 1
                title: achievement.title || "",
                year: achievement.year || new Date().getFullYear(),
              };
            })
          : [
              {
                id: 1,
                title: "",
                year: new Date().getFullYear(),
              },
            ]
      );

      if (selectedBand.bandImages && Array.isArray(selectedBand.bandImages)) {
        const existingImages = selectedBand.bandImages.map((img, index) => ({
          id: img.id || index,
          file: null,
          preview: img.url,
          isMain: Boolean(img.isMain),
          isBanner: Boolean(img.isBanner),
          filename: img.filename || `image-${index}`,
          originalName: img.originalName || img.filename,
          url: img.url,
          existing: true,
        }));
        setBandImages(existingImages);
        // setImagePreview(existingImages.map((img) => img.preview));
      } else {
        setBandImages([]);
        // setImagePreview([]);
      }

      setSelectedGenre(selectedBand?.genre || "");
      setIsFeatured(selectedBand?.isFeatured || false);

      // Set package if band has an admin user with package info
      if (selectedBand?.adminUser?.subscriptionPackage) {
        setSelectedPackage(selectedBand.adminUser.subscriptionPackage);
      } else {
        setSelectedPackage("Basic 1"); // Default package
      }

      // Na kraju, sacuvaj originalne podatke
      const originalBandData = {
        name: selectedBand.name || "",
        description: selectedBand.description || "",
        genre: selectedBand.genre || "",
        city: selectedBand.city || selectedBand.origin || "",
        founded: selectedBand.founded || selectedBand.foundedYear || "",
        members: Array.isArray(selectedBand.members)
          ? selectedBand.members.map((member, index) => {
              if (typeof member === "string") {
                return {
                  id: index + 1,
                  name: member,
                  joined: "",
                  role: "",
                  bio: "",
                };
              }
              return {
                id: member.id || index + 1,
                name: member.name || "",
                joined: member.joined || "",
                role: member.role || "",
                bio: member.bio || "",
              };
            })
          : [{ id: 1, name: "", joined: "", role: "", bio: "" }],
        discography: Array.isArray(albumsData)
          ? albumsData.map((album, index) => ({
              id: album.id || index + 1,
              name: album.title || "",
              year: album.year || "",
              type: album.type || "Studio album",
            }))
          : [{ id: 1, name: "", year: "" }],
        achievements: Array.isArray(selectedBand.achievements)
          ? selectedBand.achievements.map((achievement, index) => {
              if (typeof achievement === "string") {
                return {
                  id: index + 1,
                  title: achievement,
                  year: new Date().getFullYear(),
                };
              }
              return {
                id: achievement.id || index + 1,
                title: achievement.title || "",
                year: achievement.year || new Date().getFullYear(),
              };
            })
          : [{ id: 1, title: "", year: new Date().getFullYear() }],
        isFeatured: selectedBand.isFeatured || false,
        fbUrl: selectedBand.fbUrl || selectedBand.socialMedia?.facebook || "",
        ytUrl: selectedBand.ytUrl || selectedBand.socialMedia?.youtube || "",
        instaUrl:
          selectedBand.instaUrl || selectedBand.socialMedia?.instagram || "",
        spotifyUrl:
          selectedBand.spotifyUrl || selectedBand.socialMedia?.spotify || "",
        xUrl: selectedBand.xUrl || selectedBand.socialMedia?.twitter || "",
        appleUrl:
          selectedBand.appleMusicUrl || selectedBand.socialMedia?.apple || "",
        soundcloudUrl:
          selectedBand.soundcloudUrl ||
          selectedBand.socialMedia?.soundcloud ||
          "",
        websiteUrl:
          selectedBand.websiteUrl || selectedBand.socialMedia?.website || "",
        images: selectedBand.bandImages || [],
      };

      setOriginalData(originalBandData);
    } else if (activeModal === "addBand") {
      setOriginalData(null);
    }
  }, [selectedBand, activeModal]);

  // Helper funkcije za validaciju
  const validateForm = (formData) => {
    const newErrors = {};

    // Required fields
    if (!formData.get("bandName")?.trim()) {
      newErrors.bandName = "Naziv benda je obavezan";
    }

    if (!formData.get("foundedYear")) {
      newErrors.foundedYear = "Godina osnivanja je obavezna";
    }

    if (!formData.get("origin")?.trim()) {
      newErrors.origin = "Mesto porekla je obavezno";
    }

    if (!selectedGenre || selectedGenre.trim() === "") {
      newErrors.genre = "Žanr je obavezan";
    }

    if (!formData.get("description")?.trim()) {
      newErrors.description = "Opis benda je obavezan";
    }

    // Validate members (at least one non-empty member)
    if (Array.isArray(bandMembers)) {
      const nonEmptyMembers = bandMembers.filter((member) => {
        if (typeof member === "string") {
          return member.trim();
        } else if (typeof member === "object" && member !== null) {
          return member.name && member.name.trim();
        }
        return false;
      });
      if (nonEmptyMembers.length === 0) {
        newErrors.members = "Potreban je najmanje jedan član benda";
      }
    } else {
      newErrors.members = "Potreban je najmanje jedan član benda";
    }

    return newErrors;
  };

  const hasChanges = (formData) => {
    if (activeModal === "addBand") return true; // Novi bend uvek ima izmene
    if (!originalData) return true;

    // Poredi osnovne podatke
    const currentName = formData.get("bandName")?.trim() || "";
    const currentDescription = formData.get("description")?.trim() || "";
    const currentCity = formData.get("origin")?.trim() || "";
    const currentFounded = parseInt(formData.get("foundedYear")) || "";
    const currentFbUrl = formData.get("facebookLink")?.trim() || "";
    const currentYtUrl = formData.get("youtubeLink")?.trim() || "";
    const currentInstaUrl = formData.get("instagramLink")?.trim() || "";
    const currentSpotifyUrl = formData.get("spotifyLink")?.trim() || "";
    const currentXUrl = formData.get("xLink")?.trim() || "";
    const currentAppleUrl = formData.get("appleLink")?.trim() || "";
    const currentSoundcloudUrl = formData.get("soundcloudLink")?.trim() || "";
    const currentWebsiteUrl = formData.get("websiteLink")?.trim() || "";

    if (
      currentName !== originalData.name ||
      currentDescription !== originalData.description ||
      selectedGenre !== originalData.genre ||
      currentCity !== originalData.city ||
      currentFounded !== originalData.founded ||
      isFeatured !== originalData.isFeatured ||
      currentFbUrl !== originalData.fbUrl ||
      currentYtUrl !== originalData.ytUrl ||
      currentInstaUrl !== originalData.instaUrl ||
      currentSpotifyUrl !== originalData.spotifyUrl ||
      currentXUrl !== originalData.xUrl ||
      currentAppleUrl !== originalData.appleMusicUrl ||
      currentSoundcloudUrl !== originalData.soundcloudUrl ||
      currentWebsiteUrl !== originalData.websiteUrl
    ) {
      return true;
    }

    // Poredi članove
    const currentMembers = bandMembers.filter((m) => m.name?.trim());
    const originalMembers = originalData.members.filter((m) => m.name?.trim());

    if (currentMembers.length !== originalMembers.length) return true;

    for (let i = 0; i < currentMembers.length; i++) {
      if (currentMembers[i].name !== originalMembers[i].name) return true;
    }

    // Poredi albume
    const currentAlbums = discography.filter((a) => a.title?.trim());
    const originalAlbums = originalData.discography.filter((a) =>
      a.title?.trim()
    );

    if (currentAlbums.length !== originalAlbums.length) return true;

    for (let i = 0; i < currentAlbums.length; i++) {
      if (
        currentAlbums[i].name !== originalAlbums[i].title ||
        currentAlbums[i].year !== originalAlbums[i].year
      )
        return true;
    }

    // Poredi postignuća
    const currentAchievements = achievements.filter((a) => a.title?.trim());
    const originalAchievements = originalData.achievements.filter((a) =>
      a.title?.trim()
    );

    if (currentAchievements.length !== originalAchievements.length) return true;

    for (let i = 0; i < currentAchievements.length; i++) {
      if (
        currentAchievements[i].title !== originalAchievements[i].title ||
        currentAchievements[i].year !== originalAchievements[i].year
      )
        return true;
    }

    // Poredi slike
    const currentImages = bandImages || [];
    const originalImages = originalData.images || [];

    // Proveri broj slika
    if (currentImages.length !== originalImages.length) return true;

    // Proveri da li su dodane nove slike
    const hasNewImages = currentImages.some((img) => !img.existing);
    if (hasNewImages) return true;

    // Proveri izmene u postojećim slikama (isMain, isBanner)
    for (let i = 0; i < currentImages.length; i++) {
      const current = currentImages[i];
      const original = originalImages.find((orig) => orig.id === current.id);

      if (!original) return true; // Nova slika

      if (
        current.isMain !== Boolean(original.isMain) ||
        current.isBanner !== Boolean(original.isBanner)
      ) {
        return true;
      }
    }

    return false;
  };

  // Funkcije za upravljanje članovima (objekti sa id, name, joined, role, bio)
  const addMember = (e) => {
    e.preventDefault();
    const newId = Math.max(0, ...bandMembers.map((m) => m.id || 0)) + 1;
    const newMember = { id: newId, name: "", joined: "", role: "", bio: "" };
    setBandMembers([...bandMembers, newMember]);
  };

  const removeMember = (index) => {
    if (bandMembers.length > 1) {
      setBandMembers(bandMembers.filter((_, i) => i !== index));
    }
  };

  const updateMember = (index, field, value) => {
    const updatedMembers = [...bandMembers];
    updatedMembers[index] = {
      ...updatedMembers[index],
      [field]: value,
    };
    setBandMembers(updatedMembers);
  };

  // Funkcije za upravljanje diskografijom
  const addAlbum = (e) => {
    e.preventDefault();

    const newId = Math.max(0, ...discography.map((d) => d.id || 0)) + 1;
    const newAlbum = { id: newId, title: "", year: "", type: "Studio album" };

    const newDiscography = [...discography, newAlbum];
    setDiscography(newDiscography);
  };

  const removeAlbum = (index) => {
    if (discography.length > 1) {
      setDiscography(discography.filter((_, i) => i !== index));
    }
  };

  const updateAlbum = (index, field, value) => {
    const updatedDiscography = [...discography];
    updatedDiscography[index] = {
      ...updatedDiscography[index],
      [field]: value,
    };
    setDiscography(updatedDiscography);
  };

  // Funkcije za upravljanje postignućima
  const addAchievement = (e) => {
    e.preventDefault();

    const newId = Math.max(0, ...achievements.map((a) => a.id || 0)) + 1;
    const newAchievement = {
      id: newId,
      title: "",
      year: new Date().getFullYear(),
    };

    setAchievements([...achievements, newAchievement]);
  };

  const removeAchievement = (id) => {
    if (achievements.length > 1) {
      setAchievements(
        achievements.filter((achievement) => achievement.id !== id)
      );
    }
  };

  const updateAchievement = (id, field, value) => {
    setAchievements(
      achievements.map((achievement) =>
        achievement.id === id ? { ...achievement, [field]: value } : achievement
      )
    );
  };

  // Funkcije za upravljanje slikama
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);

    files.forEach((file, fileIndex) => {
      if (file && file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (event) => {
          // Generiši jedinstveni filename sa timestamp
          const timestamp = Date.now();
          const extension = file.name.split(".").pop();
          const filename = `${timestamp}_${fileIndex}.${extension}`;

          const newImage = {
            id: timestamp + fileIndex,
            file,
            preview: event.target.result, // Za preview koristimo data URL
            isMain: false,
            isBanner: false,
            filename: filename,
            originalName: file.name,
            existing: false,
            url: event.target.result, // Dodaj URL property
          };

          setBandImages((prev) => {
            const updated = [...prev, newImage];

            return updated;
          });

          // setImagePreview((prev) => [...prev, event.target.result]);
        };
        reader.readAsDataURL(file);
      }
    });

    // Reset input value da omogućimo upload istog fajla ponovo
    e.target.value = "";
  };

  const removeImage = (index) => {
    const imageToRemove = bandImages[index];

    if (!imageToRemove) return;

    const updatedImages = bandImages.filter((_, i) => i !== index);

    // Ako uklanjamo glavnu sliku i ima još slika, postavi prvu kao glavnu
    if (imageToRemove.isMain && updatedImages.length > 0) {
      updatedImages[0] = { ...updatedImages[0], isMain: true };
    }

    // Ako uklanjamo banner sliku, ne postavlja automatski drugu kao banner
    // Banner ostaje prazan dok korisnik ne izabere drugu sliku

    setBandImages(updatedImages);
    // setImagePreview((prev) => prev.filter((_, i) => i !== index));
  };

  // Helper funkcija za čišćenje praznih stringova
  const cleanArray = (arr) => {
    if (!Array.isArray(arr)) return [];

    return arr.filter((item) => {
      if (typeof item === "string") {
        return item.trim() !== "";
      }
      if (typeof item === "object" && item !== null) {
        return Object.values(item).some(
          (val) => val && val.toString().trim() !== ""
        );
      }
      return false;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    const htmlFormData = new FormData(e.target);

    // Validate form
    const validationErrors = validateForm(htmlFormData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }
    if (activeModal === "editBand" && !hasChanges(htmlFormData)) {
      toast.info("Nema izmena za čuvanje");
      setIsSubmitting(false);
      return;
    }

    try {
      // Create FormData for multipart/form-data request (backend expects this for file uploads)
      const formData = new FormData();

      // Add basic band data
      formData.append("name", htmlFormData.get("bandName").trim());
      formData.append("description", htmlFormData.get("description").trim());
      formData.append("genre", selectedGenre);
      formData.append("city", htmlFormData.get("origin").trim());
      formData.append(
        "founded",
        parseInt(htmlFormData.get("foundedYear")) || ""
      );
      formData.append("subscriptionPackage", selectedPackage);

      // Add JSON arrays (backend expects JSON strings for complex data)
      const cleanMembers = cleanArray(bandMembers || []);
      const cleanAlbums = cleanArray(discography || []);
      const cleanAchievements = (achievements || [])
        .filter(
          (achievement) =>
            achievement && achievement.title && achievement.title.trim() !== ""
        )
        .map((achievement) => ({
          ...achievement,
          year: parseInt(achievement.year) || new Date().getFullYear(),
        }));

      if (cleanMembers.length > 0) {
        formData.append("members", JSON.stringify(cleanMembers));
      }
      if (cleanAlbums.length > 0) {
        formData.append("discography", JSON.stringify(cleanAlbums));
      }
      if (cleanAchievements.length > 0) {
        formData.append("achievements", JSON.stringify(cleanAchievements));
      }

      // Add boolean flags (backend expects string values)
      formData.append("isFeatured", isFeatured ? "true" : "false");
      formData.append("active", "true");

      // Add optional social media links
      const facebookLink = htmlFormData.get("facebookLink")?.trim();
      const youtubeLink = htmlFormData.get("youtubeLink")?.trim();
      const instagramLink = htmlFormData.get("instagramLink")?.trim();
      const spotifyLink = htmlFormData.get("spotifyLink")?.trim();
      const xLink = htmlFormData.get("xLink")?.trim();
      const appleLink = htmlFormData.get("appleLink")?.trim();
      const soundcloudLink = htmlFormData.get("soundcloudLink")?.trim();
      const websiteLink = htmlFormData.get("websiteLink")?.trim();

      if (facebookLink && facebookLink !== "")
        formData.append("fbUrl", facebookLink);
      if (youtubeLink && youtubeLink !== "")
        formData.append("ytUrl", youtubeLink);
      if (instagramLink && instagramLink !== "")
        formData.append("instaUrl", instagramLink);
      if (spotifyLink && spotifyLink !== "")
        formData.append("spotifyUrl", spotifyLink);
      if (xLink && xLink !== "") formData.append("xUrl", xLink);
      if (appleLink && appleLink !== "")
        formData.append("appleMusicUrl", appleLink);
      if (soundcloudLink && soundcloudLink !== "")
        formData.append("soundcloudUrl", soundcloudLink);
      if (websiteLink && websiteLink !== "")
        formData.append("websiteUrl", websiteLink);

      // Handle images based on whether we're creating or editing
      if (bandImages && bandImages.length > 0) {
        const newImages = bandImages.filter((img) => img.file && !img.existing);
        const existingImages = bandImages.filter((img) => img.existing);

        // Handle new images upload
        if (newImages.length > 0) {
          let mainImageIndex = -1;
          let bannerImageIndex = -1;

          // Dodaj nove slike u FormData
          newImages.forEach((newImg, newArrayIndex) => {
            formData.append("bandImages", newImg.file);

            // Koristimo index u newImages array-u (newArrayIndex)
            if (newImg.isMain) {
              mainImageIndex = newArrayIndex;
            }
            if (newImg.isBanner) {
              bannerImageIndex = newArrayIndex;
            }
          });

          // Send indices to backend only if they are valid
          if (mainImageIndex >= 0) {
            formData.append("mainImageIndex", mainImageIndex.toString());
          }
          if (bannerImageIndex >= 0) {
            formData.append("bannerImageIndex", bannerImageIndex.toString());
          }
        }

        // For editing with existing images or mixed scenarios, use JSON for existing images
        if (activeModal === "editBand" && existingImages.length > 0) {
          const imagesData = existingImages.map((img) => ({
            id: img.id,
            filename: img.filename,
            originalName: img.originalName || img.filename,
            url: img.url,
            isMain: img.isMain,
            isBanner: img.isBanner,
            existing: true,
          }));

          formData.append("images", JSON.stringify(imagesData));
        }
      }

      for (let pair of formData.entries()) {
        console.log(`${pair[0]}:`, pair[1]);
      }

      // Call the parent handler with FormData
      await handleBandSubmit(formData);
    } catch (error) {
      console.error("Error in form submission:", error);
      setErrors({ general: error.message || "Greška pri čuvanju benda" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const setMainImage = (index) => {
    const updatedImages = bandImages.map((img, i) => ({
      ...img,
      isMain: i === index,
    }));

    setBandImages(updatedImages);
  };

  const setBannerImage = (index) => {
    const updatedImages = bandImages.map((img, i) => ({
      ...img,
      isBanner: i === index,
    }));

    setBandImages(updatedImages);
  };

  const genres = [
    "Rock",
    "Punk-Rock",
    "Punk",
    "Pop",
    "Metal",
    "Alternative",
    "Indie",
    "Electronic",
    "Hip-Hop",
    "R&B",
    "Jazz",
    "Blues",
    "Funk",
    "Soul",
    "Reggae",
    "Country",
    "Folk",
    "Classical",
  ];

  return (
    <Modal $active={activeModal === "addBand" || activeModal === "editBand"}>
      <ModalContent style={{ maxWidth: "800px", maxHeight: "90vh" }}>
        <ModalHeader>
          <ModalTitle>
            {activeModal === "editBand" ? "Izmeni Bend" : "Dodaj Novi Bend"}
          </ModalTitle>
          <ModalClose onClick={closeModal} disabled={isSubmitting}>
            <FiX />
          </ModalClose>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          {/* Osnovne informacije */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
              marginBottom: "2rem",
            }}
          >
            <FormGroup>
              <FormLabel>
                <FiMusic style={{ marginRight: "0.5rem" }} />
                Naziv Benda *
              </FormLabel>
              <FormInput
                type="text"
                name="bandName"
                defaultValue={selectedBand?.name || ""}
                placeholder="Unesite naziv benda"
                className={errors.bandName ? "error" : ""}
                required
              />
              {errors.bandName && <ErrorText>{errors.bandName}</ErrorText>}
            </FormGroup>

            <FormGroup>
              <FormLabel>
                <FiCalendar style={{ marginRight: "0.5rem" }} />
                Godina Osnivanja *
              </FormLabel>
              <FormInput
                type="number"
                name="foundedYear"
                defaultValue={
                  selectedBand?.founded || selectedBand?.foundedYear || ""
                }
                placeholder="npr. 1990"
                min="1900"
                max={new Date().getFullYear()}
                className={errors.foundedYear ? "error" : ""}
                required
              />
              {errors.foundedYear && (
                <ErrorText>{errors.foundedYear}</ErrorText>
              )}
            </FormGroup>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "1rem",
              marginBottom: "2rem",
            }}
          >
            <FormGroup>
              <FormLabel>
                <FiMapPin style={{ marginRight: "0.5rem" }} />
                Mesto Porekla *
              </FormLabel>
              <FormInput
                type="text"
                name="origin"
                defaultValue={selectedBand?.city || selectedBand?.origin || ""}
                placeholder="npr. Novi Sad, Srbija"
                className={errors.origin ? "error" : ""}
                required
              />
              {errors.origin && <ErrorText>{errors.origin}</ErrorText>}
            </FormGroup>

            <FormGroup>
              <FormLabel>Žanr *</FormLabel>
              <Relative>
                <FormSelect
                  name="genre"
                  value={selectedGenre}
                  onChange={(e) => setSelectedGenre(e.target.value)}
                  className={errors.genre ? "error" : ""}
                  required
                >
                  <option value="">Izaberite žanr...</option>
                  {genres.map((genre) => (
                    <option key={genre} value={genre}>
                      {genre}
                    </option>
                  ))}
                </FormSelect>
                <FiChevronDown
                  color="#ff6b35"
                  size={20}
                  style={{
                    position: "absolute",
                    right: 15,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              </Relative>
              {errors.genre && <ErrorText>{errors.genre}</ErrorText>}
            </FormGroup>

            <FormGroup>
              <FormLabel>Subscription Paket</FormLabel>
              <Relative>
                <FormSelect
                  name="subscriptionPackage"
                  value={selectedPackage}
                  onChange={(e) => setSelectedPackage(e.target.value)}
                >
                  {availablePackages.map((pkg) => (
                    <option key={pkg.name} value={pkg.name}>
                      {pkg.name} ({pkg.percentage}%)
                    </option>
                  ))}
                </FormSelect>
                <FiChevronDown
                  color="#ff6b35"
                  size={20}
                  style={{
                    position: "absolute",
                    right: 15,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              </Relative>
              <small style={{ color: "#cccccc", fontSize: "0.8rem" }}>
                Procenat zarade benda od prodaje
              </small>
            </FormGroup>
          </div>

          {/* Slike benda */}
          <FormGroup>
            <FormLabel>Slike Benda</FormLabel>

            {/* Upload dugme */}
            <ImageUploadButton>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                style={{
                  display: "none",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
                id="band-images-input"
              />
              <label htmlFor="band-images-input">
                <FiPlus />
                <span>Dodaj slike benda</span>
              </label>
            </ImageUploadButton>

            {/* Preview slika */}
            {bandImages.length > 0 && (
              <ImagePreviewContainer>
                {bandImages.map((image, index) => (
                  <ImagePreviewItem key={image.id || index}>
                    <ImagePreview>
                      <img
                        src={image.url || image.preview}
                        alt={`Preview ${index}`}
                      />

                      {/* Image actions */}
                      <ImageActions>
                        {!image.isMain && (
                          <ActionBtn
                            type="button"
                            onClick={() => setMainImage(index)}
                            title="Postavi kao glavnu sliku"
                            className="set-main"
                          >
                            Glavna
                          </ActionBtn>
                        )}

                        {!image.isBanner && (
                          <ActionBtn
                            type="button"
                            onClick={() => setBannerImage(index)}
                            title="Postavi kao banner sliku"
                            className="set-banner"
                          >
                            Banner
                          </ActionBtn>
                        )}

                        <ActionBtn
                          type="button"
                          onClick={() => removeImage(index)}
                          title="Ukloni sliku"
                          className="remove"
                        >
                          <FiTrash2 size={14} />
                        </ActionBtn>
                      </ImageActions>
                    </ImagePreview>

                    <ImageInfo>
                      <span>{image.filename}</span>
                      {image.isMain && (
                        <span className="main-label">Glavna</span>
                      )}
                      {image.isBanner && (
                        <span className="banner-label">Banner</span>
                      )}
                    </ImageInfo>
                  </ImagePreviewItem>
                ))}
              </ImagePreviewContainer>
            )}

            {/* Poruka kad nema slika */}
            {bandImages.length === 0 && (
              <NoImagesMessage>
                Nema dodanih slika. Prva dodana slika će biti automatski
                postavljena kao glavna.
              </NoImagesMessage>
            )}
          </FormGroup>

          {/* Opis benda */}
          <FormGroup>
            <FormLabel>Kratki Opis Benda *</FormLabel>
            <FormTextarea
              name="description"
              defaultValue={selectedBand?.description || ""}
              placeholder="Opišite bend, njihov stil, istoriju..."
              rows="4"
              className={errors.description ? "error" : ""}
              required
            />
            {errors.description && <ErrorText>{errors.description}</ErrorText>}
          </FormGroup>

          {/* Članovi benda */}
          <FormGroup>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <FormLabel>
                <FiUsers style={{ marginRight: "0.5rem" }} />
                Članovi Benda *
              </FormLabel>
              <Button type="button" variant="success" onClick={addMember}>
                <FiPlus /> Dodaj Člana
              </Button>
            </div>

            {bandMembers.map((member, index) => (
              <div
                key={member.id || index}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "0.5rem",
                  marginBottom: "0.5rem",
                  alignItems: "center",
                }}
              >
                <FormInput
                  type="text"
                  placeholder="Ime člana"
                  value={member.name}
                  onChange={(e) => updateMember(index, "name", e.target.value)}
                  className={errors.members ? "error" : ""}
                  required
                />
                {bandMembers.length > 1 && (
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => removeMember(index)}
                    style={{
                      padding: "0.5rem",
                      minWidth: 42,
                      maxWidth: 42,
                      maxHeight: 42,
                      minHeight: 42,
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FiTrash2 size={16} />
                  </Button>
                )}
              </div>
            ))}
            {errors.members && <ErrorText>{errors.members}</ErrorText>}
          </FormGroup>

          {/* Diskografija (albums) */}
          <FormGroup>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <FormLabel>Diskografija</FormLabel>
              <Button type="button" variant="success" onClick={addAlbum}>
                <FiPlus /> Dodaj Album
              </Button>
            </div>

            {discography.map((album, index) => (
              <div
                key={album.id || index}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "0.5rem",
                  marginBottom: "0.5rem",
                  alignItems: "center",
                }}
              >
                <FormInput
                  type="text"
                  placeholder="Naziv albuma"
                  value={album.title}
                  onChange={(e) => updateAlbum(index, "title", e.target.value)}
                />
                <FormInput
                  type="number"
                  placeholder="Godina"
                  value={album.year}
                  onChange={(e) => updateAlbum(index, "year", e.target.value)}
                  min="1900"
                  max={new Date().getFullYear()}
                  style={{ maxWidth: "120px" }}
                />
                {discography.length > 1 && (
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => removeAlbum(index)}
                    style={{
                      padding: "0.5rem",
                      minWidth: 42,
                      maxWidth: 42,
                      maxHeight: 42,
                      minHeight: 42,
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FiTrash2 size={16} />
                  </Button>
                )}
              </div>
            ))}
          </FormGroup>

          {/* Postignuća */}
          <FormGroup>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <FormLabel>
                <FiAward style={{ marginRight: "0.5rem" }} />
                Postignuća
              </FormLabel>
              <Button type="button" variant="success" onClick={addAchievement}>
                <FiPlus /> Dodaj Postignuće
              </Button>
            </div>

            {achievements.map((achievement) => (
              <div
                key={achievement.id}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "0.5rem",
                  marginBottom: "0.5rem",
                  alignItems: "center",
                }}
              >
                <FormInput
                  type="text"
                  placeholder="Naziv postignuća"
                  value={achievement.title}
                  onChange={(e) =>
                    updateAchievement(achievement.id, "title", e.target.value)
                  }
                  style={{ flex: 1 }}
                />
                <FormInput
                  type="number"
                  placeholder="Godina"
                  value={achievement.year}
                  min="1900"
                  max={new Date().getFullYear()}
                  onChange={(e) =>
                    updateAchievement(
                      achievement.id,
                      "year",
                      parseInt(e.target.value)
                    )
                  }
                  style={{ width: "100px" }}
                />
                {achievements.length > 1 && (
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => removeAchievement(achievement.id)}
                    style={{
                      padding: "0.5rem",
                      minWidth: 42,
                      maxWidth: 42,
                      maxHeight: 42,
                      minHeight: 42,
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FiTrash2 size={16} />
                  </Button>
                )}
              </div>
            ))}
          </FormGroup>

          {/* Social Media linkovi (optional) */}
          <FormGroup>
            <FormLabel>Social Media Linkovi</FormLabel>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
              }}
            >
              <FormInput
                type="url"
                name="spotifyLink"
                defaultValue={
                  selectedBand?.spotifyUrl ||
                  selectedBand?.socialMedia?.spotify ||
                  ""
                }
                placeholder="Spotify link (opciono)"
              />
              <FormInput
                type="url"
                name="youtubeLink"
                defaultValue={
                  selectedBand?.ytUrl ||
                  selectedBand?.socialMedia?.youtube ||
                  ""
                }
                placeholder="YouTube link (opciono)"
              />
              <FormInput
                type="url"
                name="facebookLink"
                defaultValue={
                  selectedBand?.fbUrl ||
                  selectedBand?.socialMedia?.facebook ||
                  ""
                }
                placeholder="Facebook link (opciono)"
              />
              <FormInput
                type="url"
                name="instagramLink"
                defaultValue={
                  selectedBand?.instaUrl ||
                  selectedBand?.socialMedia?.instagram ||
                  ""
                }
                placeholder="Instagram link (opciono)"
              />
              <FormInput
                type="url"
                name="xLink"
                defaultValue={
                  selectedBand?.xUrl || selectedBand?.socialMedia?.x || ""
                }
                placeholder="X (Twitter) link (opciono)"
              />
              <FormInput
                type="url"
                name="appleLink"
                defaultValue={
                  selectedBand?.appleMusicUrl ||
                  selectedBand?.socialMedia?.apple ||
                  ""
                }
                placeholder="Apple Music link (opciono)"
              />
              <FormInput
                type="url"
                name="soundcloudLink"
                defaultValue={
                  selectedBand?.soundcloudUrl ||
                  selectedBand?.socialMedia?.soundcloud ||
                  ""
                }
                placeholder="SoundCloud link (opciono)"
              />
              <FormInput
                type="url"
                name="websiteLink"
                defaultValue={
                  selectedBand?.websiteUrl ||
                  selectedBand?.socialMedia?.website ||
                  ""
                }
                placeholder="Website link (opciono)"
              />
            </div>
          </FormGroup>

          {/* Status checkboxes */}
          <FormGroup>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                color: "#ff6b35",
              }}
            >
              <input
                type="checkbox"
                name="isFeatured"
                checked={isFeatured}
                onChange={(e) => setIsFeatured(e.target.checked)}
              />
              Istakni bend (Featured)
            </label>
          </FormGroup>

          {errors.general && <ErrorMessage>{errors.general}</ErrorMessage>}

          {/* Dugmad */}
          <div style={{ display: "flex", gap: "1rem", marginTop: "2rem" }}>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting
                ? "Čuvam..."
                : activeModal === "editBand"
                ? "Sačuvaj Izmene"
                : "Dodaj Bend"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={closeModal}
              disabled={isSubmitting}
            >
              Otkaži
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default BandModal;

// Postojeći stilovi ostaju isti
export const Modal = styled.div`
  display: ${(props) => (props.$active ? "flex" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  z-index: 2000;
  backdrop-filter: blur(5px);
  align-items: center;
  justify-content: center;
`;

export const ModalContent = styled.div`
  background: #1a1a1a;
  border-radius: 15px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  border: 2px solid #ff6b35;
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

export const ModalTitle = styled.h3`
  color: #ff6b35;
  font-size: 1.5rem;
  font-weight: bold;
`;

export const ModalClose = styled.button`
  background: none;
  border: none;
  color: #ff6b35;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  &:hover:not(:disabled) {
    transform: rotate(90deg);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

export const FormLabel = styled.label`
  display: block;
  color: #ff6b35;
  font-weight: bold;
  margin-bottom: 0.5rem;
`;
export const Relative = styled.div`
  position: relative;
`;
export const FormInput = styled.input`
  width: 100%;
  padding: 0.8rem;
  background: #333;
  border: 1px solid #555;
  border-radius: 8px;
  color: #ffffff;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }

  &.error {
    border-color: #e74c3c;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const FormSelect = styled.select`
  width: 100%;
  padding: 0.8rem;
  background: #333;
  border: 1px solid #555;
  border-radius: 8px;
  color: #ffffff;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }

  &.error {
    border-color: #e74c3c;
  }
`;

export const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.8rem;
  background: #333;
  border: 1px solid #555;
  border-radius: 8px;
  color: #ffffff;
  transition: border-color 0.3s ease;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }

  &.error {
    border-color: #e74c3c;
  }
`;

export const Button = styled.button`
  padding: 0.8rem 1.2rem;
  min-height: 3rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${(props) => {
    switch (props.variant) {
      case "primary":
        return "#ff6b35";
      case "secondary":
        return "#666";
      case "danger":
        return "#e74c3c";
      case "success":
        return "#27ae60";
      default:
        return "#ff6b35";
    }
  }};
  color: #ffffff;

  &:hover:not(:disabled) {
    background: ${(props) => {
      switch (props.variant) {
        case "primary":
          return "#e55a2b";
        case "secondary":
          return "#555";
        case "danger":
          return "#c0392b";
        case "success":
          return "#229954";
        default:
          return "#e55a2b";
      }
    }};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ImageUpload = styled.div`
  border: 2px dashed #555;
  border-radius: 10px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  color: #cccccc;

  &:hover {
    border-color: #ff6b35;
    color: #ff6b35;
  }

  input[type="file"] {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }

  svg {
    font-size: 2rem;
    margin-bottom: 0.5rem;
    display: block;
  }

  span {
    font-weight: 500;
  }
`;

export const ErrorMessage = styled.div`
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid #e74c3c;
  color: #e74c3c;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
`;

export const ErrorText = styled.div`
  color: #e74c3c;
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

// Novi styled components za image upload
export const ImageUploadButton = styled.div`
  border: 2px dashed #555;
  border-radius: 10px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #cccccc;
  margin-bottom: 1rem;
  position: relative;

  &:hover {
    border-color: #ff6b35;
    color: #ff6b35;
  }

  label {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;

    svg {
      font-size: 1.5rem;
    }

    span {
      font-weight: 500;
    }
  }
`;

export const ImagePreviewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

export const ImagePreviewItem = styled.div`
  background: #1a1a1a;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #333;
`;

export const ImagePreview = styled.div`
  position: relative;
  aspect-ratio: 16/9;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const MainImageBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: #ff6b35;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: bold;
`;

export const ImageActions = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
`;

export const ActionBtn = styled.button`
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.7rem;
  transition: background 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }

  &.set-main {
    background: rgba(255, 107, 53, 0.8);

    &:hover {
      background: rgba(255, 107, 53, 1);
    }
  }

  &.remove {
    background: rgba(231, 76, 60, 0.8);

    &:hover {
      background: rgba(231, 76, 60, 1);
    }
  }
`;

export const MoveButtons = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
`;

export const MoveBtn = styled.button`
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  transition: background 0.3s;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

export const ImageInfo = styled.div`
  padding: 8px;

  span {
    display: block;
    font-size: 0.8rem;
    color: #ccc;

    &.main-label {
      color: #ff6b35;
      font-weight: bold;
      margin-top: 4px;
    }

    &.banner-label {
      color: #9b59b6;
      font-weight: bold;
      margin-top: 4px;
    }
  }
`;

export const NoImagesMessage = styled.div`
  text-align: center;
  color: #888;
  font-style: italic;
  padding: 2rem;
  border: 1px dashed #333;
  border-radius: 8px;
`;
